/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The two React ends of the view-state store (src/lib/viewState.ts), for a
 * component that keeps a slot of its UI state per history entry.
 *
 *   const restored = useInitialViewState('overview', parseOverview);
 *   const [query, setQuery] = useState(() => restored?.query ?? '');
 *   …
 *   useViewStateSync({ slot: 'overview', value, isDefault, parse, onLateRestore });
 *
 * `useInitialViewState` reads the in-memory record ONCE, at the first render:
 * an in-tab Back/Forward renders the page as it was left, at once. It is null
 * on every hydrating render (the map is empty until this document writes it).
 *
 * `useViewStateSync` saves the value under the current entry whenever it
 * changes, and after mount applies a record a full document load found in
 * sessionStorage (a reload, a Back/Forward across documents) through
 * `onLateRestore` — the component's own setters, called from an effect, never
 * during render. Once that restored state has been committed it tells the
 * store, which is what the scroll manager waits for before it puts the page
 * back where the visitor left it.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  markFirstLoadApplied,
  noteViewStateLocation,
  readViewSlot,
  takeFirstLoadSlot,
  writeViewSlot,
} from '../lib/viewState';

const pathOf = (location: { pathname: string; search: string }): string =>
  `${location.pathname}${location.search}`;

/** The slot as this document left it for the current entry, parsed — first render only. */
export function useInitialViewState<T>(slot: string, parse: (raw: unknown) => T | null): T | null {
  const location = useLocation();
  const [initial] = useState<T | null>(() => {
    const raw = readViewSlot(location.key, pathOf(location), slot);
    return raw === undefined ? null : parse(raw);
  });
  return initial;
}

export interface ViewStateSyncOptions<T> {
  slot: string;
  /** The slot's current value — JSON-serialisable. */
  value: T;
  /** The value is the slot's initial one: nothing is stored for it. */
  isDefault: boolean;
  /**
   * False while something the page shows above the restored content is still
   * on its way (it would move that content after the scroll manager placed
   * it): a first-load restore is reported applied only once this is true.
   * Default true.
   */
  ready?: boolean;
  /** Validates what storage gave back; null = unusable (nothing is applied). */
  parse: (raw: unknown) => T | null;
  /** Applies a value restored after mount — set state here, nothing else. */
  onLateRestore: (value: T) => void;
}

export function useViewStateSync<T>({
  slot,
  value,
  isDefault,
  ready = true,
  parse,
  onLateRestore,
}: ViewStateSyncOptions<T>): void {
  const location = useLocation();
  const key = location.key;
  const path = pathOf(location);

  // The mount effect below runs once; these follow the latest render.
  const onLateRestoreRef = useRef(onLateRestore);
  onLateRestoreRef.current = onLateRestore;
  const parseRef = useRef(parse);
  parseRef.current = parse;

  // What the mount effect took from the store, kept for StrictMode's second
  // run of it (which must apply the same value, not ask again), and the entry
  // it belongs to. Undefined until taken; null when there was nothing.
  const takenRef = useRef<{ key: string; value: T | null } | null | undefined>(undefined);
  // True from the late restore until its state has been committed: the save
  // below must not store the defaults the first commit still shows.
  const latePendingRef = useRef(false);
  const [appliedTick, setAppliedTick] = useState(0);

  useEffect(() => {
    noteViewStateLocation(key, path);
    if (takenRef.current === undefined) {
      const raw = takeFirstLoadSlot(key, path, slot);
      takenRef.current = raw === undefined ? null : { key, value: parseRef.current(raw) };
    }
    const taken = takenRef.current;
    if (!taken) return;
    latePendingRef.current = true;
    if (taken.value !== null) onLateRestoreRef.current(taken.value);
    // Batched with the component's own updates: the effect below runs after
    // the commit that shows the restored state.
    setAppliedTick((n) => n + 1);
    // Once, on mount: a first-load restore belongs to the entry the page was loaded on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const taken = takenRef.current;
    if (appliedTick === 0 || !taken) return;
    latePendingRef.current = false;
    if (ready) markFirstLoadApplied(taken.key, slot);
  }, [appliedTick, slot, ready]);

  // Compared as JSON: a new array with the same contents is no change.
  const json = JSON.stringify(value);
  useEffect(() => {
    if (latePendingRef.current) return;
    writeViewSlot(key, path, slot, JSON.parse(json) as unknown, isDefault);
  }, [key, path, slot, json, isDefault]);
}
